import { Category } from '../../community/shared/models/category.model';
import {
    ActionType,
    CommunityItem,
    CommunityItemStatus,
    CommunityItemType,
    CommunityProjectOption
} from '../../community/shared/models/community-item.model';

interface JsonApiResource {
    id?: string | number | null;
    type?: string;
    attributes?: Record<string, unknown>;
}

export interface CommunityItemInput {
    type: CommunityItemType;
    title: string;
    description: string;
    buttonText: string;
    actionType: ActionType;
    destination: string;
    status: CommunityItemStatus;
    order: number;
}

const TYPE_TO_API: Record<CommunityItemType, string> = {
    webinar: 'webinar',
    resource: 'resource',
    project: 'project',
    event: 'event',
    video: 'video',
    external_link: 'external_link'
};

const TYPE_TO_CATEGORY: Record<CommunityItemType, string> = {
    webinar: 'cat-webinar',
    resource: 'cat-resource',
    project: 'cat-project',
    event: 'cat-event',
    video: 'cat-video',
    external_link: 'cat-external'
};

export function mapCommunitiesResponse(response: unknown): CommunityItem[] {
    return extractResources(response).map(mapResourceToItem);
}

export function mapCommunityResponse(response: unknown): CommunityItem | null {
    const resources = extractResources(response);
    return resources[0] ? mapResourceToItem(resources[0]) : null;
}

export function mapActiveProjectsResponse(response: unknown): CommunityProjectOption[] {
    return extractResources(response)
        .map((resource) => ({
            id: String(resource.id ?? ''),
            name: String(resource.attributes?.['name'] ?? '').trim()
        }))
        .filter((project) => project.id && project.name);
}


export function toCommunityRequestBody(
    item: CommunityItemInput,
    id?: string | null
): { data: Record<string, unknown> } {
    const data: Record<string, unknown> = {
        type: 'communities',
        attributes: {
            community_type: TYPE_TO_API[item.type],
            title: item.title,
            description: item.description,
            button_text: item.buttonText,
            destination: item.destination,
            action_type: item.actionType,
            status: item.status,
            display_order: item.order
        }
    };

    if (id) {
        data['id'] = /^\d+$/.test(id) ? Number(id) : id;
    }

    return { data };
}

export function toCommunityItemInput(item: CommunityItem): CommunityItemInput {
    return {
        type: item.type,
        title: item.title,
        description: item.description,
        buttonText: item.buttonText,
        actionType: item.actionType,
        destination: item.destination,
        status: item.status === 'scheduled' ? 'draft' : item.status,
        order: item.order
    };
}

export function getCategoryIdForType(type: CommunityItemType): string {
    return TYPE_TO_CATEGORY[type] ?? '';
}

function extractResources(response: unknown): JsonApiResource[] {
    if (!response || typeof response !== 'object') {
        return [];
    }

    const data = (response as { data?: unknown }).data;

    if (Array.isArray(data)) {
        return data.filter((entry): entry is JsonApiResource => !!entry && typeof entry === 'object');
    }

    if (data && typeof data === 'object') {
        return [data as JsonApiResource];
    }

    return [];
}

function mapResourceToItem(resource: JsonApiResource): CommunityItem {
    const attributes = resource.attributes ?? {};
    const type = mapApiType(String(attributes['community_type'] ?? ''));
    const status = mapApiStatus(String(attributes['status'] ?? ''), attributes);

    return {
        id: String(resource.id ?? ''),
        order: Number(attributes['display_order'] ?? 0),
        type,
        title: String(attributes['title'] ?? ''),
        description: String(attributes['description'] ?? ''),
        categoryId: TYPE_TO_CATEGORY[type] ?? '',
        imageUrl: String(attributes['image_url'] ?? attributes['imageUrl'] ?? ''),
        buttonText: String(attributes['button_text'] ?? ''),
        actionType: mapApiActionType(String(attributes['action_type'] ?? 'internal')),
        destination: String(attributes['destination'] ?? ''),
        status,
        visible: status === 'published',
        updatedAt: String(attributes['updated_at'] ?? new Date().toISOString()),
        updatedBy: String(attributes['updated_by'] ?? attributes['updated_by_name'] ?? 'Admin')
    };
}

function mapApiType(value: string): CommunityItemType {
    const normalized = value.trim().toLowerCase().replace(/\s+/g, '_');

    const fromLabel = Object.entries(TYPE_TO_API).find(
        ([, label]) => label.toLowerCase() === value.trim().toLowerCase()
    )?.[0] as CommunityItemType | undefined;

    if (fromLabel) {
        return fromLabel;
    }

    const aliases: Record<string, CommunityItemType> = {
        webinar: 'webinar',
        resource: 'resource',
        project: 'project',
        event: 'event',
        video: 'video',
        external_link: 'external_link',
        external: 'external_link'
    };

    return aliases[normalized] ?? 'resource';
}

function mapApiStatus(value: string, attributes: Record<string, unknown>): CommunityItemStatus {
    const raw = value || String(attributes['state'] ?? '');
    const normalized = raw.trim().toLowerCase();

    if (normalized === 'published' || normalized === 'draft' || normalized === 'scheduled') {
        return normalized;
    }

    return 'draft';
}

function mapApiActionType(value: string): ActionType {
    return value === 'external' ? 'external' : 'internal';
}

export function getDefaultCategories(): Category[] {
    return [
        {
            id: 'cat-webinar',
            name: 'Webinar',
            colorHex: '#4F46E5',
            icon: 'fa-video-camera',
            description: 'Live sessions and webinars'
        },
        // {
        //     id: 'cat-resource',
        //     name: 'Resource',
        //     colorHex: '#E8622C',
        //     icon: 'fa-folder',
        //     description: 'Guides, FAQs and resources'
        // },
        {
            id: 'cat-project',
            name: 'Project',
            colorHex: '#0F9B8E',
            icon: 'fa-bullseye',
            description: 'Mentoring projects'
        },
        {
            id: 'cat-event',
            name: 'Event',
            colorHex: '#8B5CF6',
            icon: 'fa-calendar',
            description: 'Events and meetups'
        },
        // {
        //     id: 'cat-video',
        //     name: 'Video',
        //     colorHex: '#0EA5E9',
        //     icon: 'fa-play-circle',
        //     description: 'Videos and recordings'
        // },
        {
            id: 'cat-external',
            name: 'External Link',
            colorHex: '#64748B',
            icon: 'fa-link',
            description: 'External websites and links'
        }
    ];
}
